// World map component used by the Geo Section.
//
// Built on `react-simple-maps` (a thin React layer over d3-geo + topojson)
// and the Natural Earth 1:110m country atlas from `world-atlas`. This
// gives us geographically-accurate country shapes for free — no more
// hand-rolled simplified polygons — and a `<ZoomableGroup>` that
// supports panning and pinch / wheel zoom out of the box.
//
// On top of that we layer:
//   * Bubble markers per (lon, lat) point, scaled by headcount.
//   * Floating zoom controls (+ / − / Reset) anchored top-right of the
//     map area.
//   * A `focusCountry` prop that programmatically zooms + centres the
//     map on a country's bounds and dims the rest of the world.
//
// All on-screen sizes (bubble radius, stroke width, label font) are
// counter-scaled by the current zoom so they stay readable when zoomed in.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from 'react-simple-maps';

import {
  COUNTRIES,
  boundsForCountry,
  centroidForCountryFeature,
  type CountryFeature,
} from './worldAtlas';
import { ACCENTS } from '../primitives/peopleInsightsTokens';

// ---------------------------------------------------------------- public API

/** [longitude, latitude]. Tuple is `readonly` to stay compatible with the
 * existing helpers in `./geoData.ts` (`coordsForLocation`,
 * `centroidForCountry`) which both return readonly tuples. */
export type LonLat = readonly [number, number];

export interface GeoPoint {
  /** Stable identifier for react keys (e.g. the raw location string). */
  id: string;
  /** Display name (used in tooltip). */
  label: string;
  /** Headcount at this point. */
  count: number;
  /** Geographic position (lon, lat). */
  coord: LonLat;
  /** Whether this point should be highlighted (e.g. currently selected). */
  highlighted?: boolean;
}

export interface WorldMapProps {
  points: GeoPoint[];
  onPointClick?: (point: GeoPoint) => void;
  /** Bubble fill colour. */
  bubbleColor?: string;
  /** Optional aria-label for the root SVG. */
  ariaLabel?: string;
  /** Min/max bubble radius in unscaled SVG units. */
  minRadius?: number;
  maxRadius?: number;
  /**
   * When provided, the map programmatically zooms + centres on this
   * country and dims everything else. Accepts the country's display
   * name; it's matched tolerantly (handles "USA" / "United States" /
   * "Czech Republic" → "Czechia" etc.).
   */
  focusCountry?: string | null;
}

// --------------------------------------------------------------- constants

const VIEWBOX_W = 800;
const VIEWBOX_H = 420;

const MIN_ZOOM = 1;
const MAX_ZOOM = 12;
const ZOOM_STEP = 1.6;

const DEFAULT_CENTER: [number, number] = [10, 20];
const DEFAULT_ZOOM = 1;

interface ViewState {
  coordinates: [number, number];
  zoom: number;
}

const DEFAULT_VIEW: ViewState = {
  coordinates: DEFAULT_CENTER,
  zoom: DEFAULT_ZOOM,
};

// --------------------------------------------------------------- component

const WorldMap: React.FC<WorldMapProps> = ({
  points,
  onPointClick,
  bubbleColor = ACCENTS.indigo.main,
  ariaLabel = 'Global headcount distribution',
  minRadius = 5,
  maxRadius = 26,
  focusCountry = null,
}) => {
  const theme = useTheme();
  const [view, setView] = useState<ViewState>(DEFAULT_VIEW);

  // Track the last focus we honoured so the user can still pan/zoom
  // freely afterwards without us snapping back on every render.
  const lastFocusRef = useRef<string | null>(null);

  useEffect(() => {
    if (focusCountry === lastFocusRef.current) return;
    lastFocusRef.current = focusCountry ?? null;

    if (!focusCountry) {
      setView(DEFAULT_VIEW);
      return;
    }
    const bounds = boundsForCountry(focusCountry);
    const centroid = centroidForCountryFeature(focusCountry);
    if (!bounds || !centroid) return;
    const [[w, s], [e, n]] = bounds;
    const lonSpan = Math.max(e - w, 0.5);
    const latSpan = Math.max(n - s, 0.5);
    // Treat zoom = 1 as "the world fills ~360° lon / 180° lat" with some
    // margin. The /160 / /80 numbers were tuned empirically against the
    // viewBox — they leave roughly 10% padding around the country.
    const zoom = Math.min(
      MAX_ZOOM,
      Math.max(1.4, Math.min(160 / lonSpan, 80 / latSpan)),
    );
    setView({ coordinates: centroid, zoom });
  }, [focusCountry]);

  const focusFeature: CountryFeature | null = useMemo(() => {
    if (!focusCountry) return null;
    const norm = focusCountry.trim().toLowerCase();
    return (
      (COUNTRIES.features.find((f) => {
        const fname = String(f.properties?.name ?? '').toLowerCase();
        return (
          fname === norm ||
          fname.includes(norm) ||
          norm.includes(fname)
        );
      }) as CountryFeature | undefined) ?? null
    );
  }, [focusCountry]);

  const maxCount = useMemo(() => {
    if (points.length === 0) return 0;
    return Math.max(...points.map((p) => p.count));
  }, [points]);

  const inv = 1 / view.zoom;

  // --- zoom controls ------------------------------------------------------

  const setZoom = (next: number) => {
    setView((v) => ({ ...v, zoom: clamp(next, MIN_ZOOM, MAX_ZOOM) }));
  };
  const zoomIn = () => setZoom(view.zoom * ZOOM_STEP);
  const zoomOut = () => setZoom(view.zoom / ZOOM_STEP);
  const resetView = () => {
    lastFocusRef.current = null;
    setView(DEFAULT_VIEW);
  };

  // --- render -------------------------------------------------------------

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor: '#F8FAFC',
        borderRadius: 1.5,
        overflow: 'hidden',
      }}
      aria-label={ariaLabel}
    >
      <ComposableMap
        projection="geoEqualEarth"
        projectionConfig={{
          // 155 fits the world neatly inside the 800x420 viewBox.
          scale: 155,
          center: [0, 0],
        }}
        width={VIEWBOX_W}
        height={VIEWBOX_H}
        style={{ width: '100%', height: '100%', display: 'block' }}
      >
        <ZoomableGroup
          center={view.coordinates}
          zoom={view.zoom}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          onMoveEnd={(next) => setView(next)}
        >
          {/* Country shapes */}
          <Geographies geography={COUNTRIES}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const isFocus =
                  Boolean(focusFeature) && geo.id === focusFeature?.id;
                const dim = Boolean(focusFeature) && !isFocus;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    style={{
                      default: {
                        fill: isFocus ? ACCENTS.indigo.surface : '#E2E8F0',
                        fillOpacity: dim ? 0.45 : 1,
                        stroke: isFocus ? ACCENTS.indigo.main : '#FFFFFF',
                        strokeWidth: (isFocus ? 1.2 : 0.5) * inv,
                        outline: 'none',
                        transition: 'fill 200ms ease, fill-opacity 200ms ease',
                      },
                      hover: {
                        fill: isFocus
                          ? ACCENTS.indigo.surface
                          : alpha(ACCENTS.indigo.main, 0.18),
                        fillOpacity: 1,
                        stroke: ACCENTS.indigo.main,
                        strokeWidth: 0.8 * inv,
                        outline: 'none',
                        cursor: 'default',
                      },
                      pressed: {
                        fill: ACCENTS.indigo.surface,
                        outline: 'none',
                      },
                    }}
                  />
                );
              })
            }
          </Geographies>

          {/* Bubbles */}
          {points.map((p) => {
            const ratio = maxCount > 0 ? Math.sqrt(p.count / maxCount) : 0;
            const baseR = Math.max(minRadius, ratio * maxRadius);
            const r = baseR * inv;
            const isClickable = Boolean(onPointClick);
            const labelFont =
              Math.max(8, Math.min(11, baseR * 0.55)) * inv;
            const showLabel = baseR >= 11;

            return (
              <Marker
                key={p.id}
                // react-simple-maps' Marker types require a mutable tuple,
                // while our LonLat is readonly — spread to satisfy both.
                coordinates={[p.coord[0], p.coord[1]]}
                onClick={isClickable ? () => onPointClick?.(p) : undefined}
                style={{
                  default: { cursor: isClickable ? 'pointer' : 'default' },
                  hover: { cursor: isClickable ? 'pointer' : 'default' },
                  pressed: {},
                }}
              >
                <Tooltip
                  title={`${p.label}: ${p.count.toLocaleString()}`}
                  arrow
                  enterDelay={150}
                  componentsProps={{
                    tooltip: { sx: { fontSize: '0.75rem' } },
                  }}
                >
                  <g>
                    {/* Soft halo */}
                    <circle
                      r={r + 5 * inv}
                      fill={bubbleColor}
                      opacity={0.18}
                    />
                    <circle
                      r={r}
                      fill={bubbleColor}
                      opacity={p.highlighted ? 1 : 0.92}
                      stroke={p.highlighted ? '#fff' : 'none'}
                      strokeWidth={p.highlighted ? 2 * inv : 0}
                    />
                    {showLabel && (
                      <text
                        textAnchor="middle"
                        y={labelFont * 0.35}
                        fontSize={labelFont}
                        fontWeight={700}
                        fill="#fff"
                        pointerEvents="none"
                      >
                        {formatCompactCount(p.count)}
                      </text>
                    )}
                  </g>
                </Tooltip>
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>

      {/* Floating zoom controls */}
      <Stack
        direction="column"
        spacing={0.5}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          backgroundColor: '#fff',
          borderRadius: 1.5,
          boxShadow: theme.shadows[2],
          border: '1px solid',
          borderColor: 'divider',
          p: 0.25,
        }}
      >
        <Tooltip title="Zoom in" placement="left" arrow>
          <span>
            <IconButton
              size="small"
              onClick={zoomIn}
              disabled={view.zoom >= MAX_ZOOM}
              aria-label="Zoom in"
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Zoom out" placement="left" arrow>
          <span>
            <IconButton
              size="small"
              onClick={zoomOut}
              disabled={view.zoom <= MIN_ZOOM}
              aria-label="Zoom out"
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Reset view" placement="left" arrow>
          <span>
            <IconButton
              size="small"
              onClick={resetView}
              disabled={
                view.zoom === DEFAULT_ZOOM &&
                view.coordinates[0] === DEFAULT_CENTER[0] &&
                view.coordinates[1] === DEFAULT_CENTER[1]
              }
              aria-label="Reset view"
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {/* Zoom level badge */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          px: 1,
          py: 0.25,
          fontSize: '0.7rem',
          fontWeight: 600,
          color: 'text.secondary',
          backgroundColor: alpha('#fff', 0.92),
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        Zoom × {view.zoom.toFixed(1)}
      </Box>
    </Box>
  );
};

// ----------------------------------------------------------------- helpers

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}

function formatCompactCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

export default WorldMap;
